import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {AppConfigService} from '../../../Services/app-config.service';
import {FixedAssetsPostingService} from '../../../Services/fixed-assets-posting.service';
import {FixedAssetConfigModel} from '../../../DataModels/fixed-asset-config-model';
import {NotificationService} from '../../../Services/notification.service';
import {forkJoin, switchMap} from 'rxjs';
import {ProrateConvention} from '../../../DataModels/depreciation-models';
import {FixedAssetsDataSourceService, FixedAssetSearch} from '../../../Services/fixed-assets-data-source.service';
import {FixedAssetsService} from '../../../Services/fixed-assets.service';
import {FixedAssetsConfigService} from '../../../Services/fixed-assets-config.service';
import {PagerStateService} from '../../../Services/pager-state.service';

@Component({
  selector: 'app-fixed-asset-list',
  templateUrl: './fixed-asset-list.component.html',
  styleUrls: ['./fixed-asset-list.component.css'],
  standalone: false
})
export class FixedAssetListComponent implements OnInit {
  list: any[] = [];
  isLoaded = false;
  // simple view-model for header controls
  vm: any = {
    searchHidden: false,
    search: {
      searchText: '',
      fromDate: null as string | Date | null,
      toDate: null as string | Date | null,
      activeOnly: false,
      location_id: ''
    },
  };
  // Precompute localized labels for template usage (avoid $localize in template expressions)
  yesLabel: string = $localize`:Yes@@yes:Yes`;
  noLabel: string = $localize`:No@@no:No`;

  // header buttons via invoice-list style; no shared header buttons used here anymore

  // DataTable state
  page = 1;
  pageSize: number;
  pageSizeOptions: number[] = [];
  totalRecords = 0;
  startIndex = 0;
  endIndex = 0;
  filterText = '';

  // selection state
  checkAll = false;

  // Grid values are fetched from the API via FixedAssetsDataSourceService

  // Config cache and latest completed period end (ISO) for selection gating
  private config?: FixedAssetConfigModel | any;
  private latestCompletedEndIso: string | null = null;

  constructor(
    private ds: FixedAssetsDataSourceService,
    private assets: FixedAssetsService,
    private router: Router,
    private titleService: Title,
    private appConfig: AppConfigService,
    private posting: FixedAssetsPostingService,
    private notify: NotificationService,
    private faConfigApi: FixedAssetsConfigService,
    private pagerStatus: PagerStateService,
  ) {
  }

  ngOnInit(): void {
    this.titleService.setTitle($localize`:Fixed Assets@@fixedAssets:Fixed Assets`);
    // paging defaults
    this.pageSizeOptions = this.appConfig.gridPageSizeSelection || [10, 25, 50, 100];
    this.pageSize = this.appConfig.ListPageSize || 10;

    // Restore pager state from enhanced per-tab cache (sessionStorage + memory)
    try {
      const key = this.pagerStatus.buildKey(this.router.url);
      const restored = this.pagerStatus.restoreState(key);
      if (restored) {
        this.page = restored.lastPage || this.page;
        this.pageSize = restored.lastPageSize || this.pageSize;
        this.startIndex = restored.startIndex || this.startIndex;
        this.endIndex = restored.endIndex || this.endIndex;
      }
    } catch {
    }

    // initial fetch via server-style search
    // also load config to determine posting convention for selection rules (API-based)
    this.faConfigApi.get().subscribe(cfg => {
      this.config = cfg;
      this.recomputeLatestCompletedEnd();
      this.search(true);
    }, _ => {
      // even if config fails, proceed with defaults
      this.recomputeLatestCompletedEnd();
      this.search(true);
    });
  }

  hideSearch(): void {
    this.vm.searchHidden = !this.vm.searchHidden;
  }

  onAdd() {
    this.router.navigate(['/fixed-assets/fixed-asset-form', 'new']);
  }

  onEdit(id: string) {
    this.router.navigate(['/fixed-assets/fixed-asset-form', id]);
  }

  onClose(): void {
    // navigate back to a sensible default, e.g., home dashboard
    this.router.navigate(['/']);
  }

  // Selection handlers
  selectAllRows(checked: boolean): void {
    const rows = this.list || [];
    // Only (de)select rows that are eligible for posting for the run date
    this.list = rows.map(r => ({
      ...r,
      selected: this.canSelect(r) ? checked : false,
    } as any));
    this.recomputeHeaderCheck();
  }

  selectRow(id: string, checked: boolean): void {
    const idx = this.list.findIndex(r => this.getId(r) === String(id));
    if (idx >= 0) {
      // Guard against selecting ineligible rows
      const row = this.list[idx];
      (this.list[idx] as any).selected = this.canSelect(row) ? checked : false;
    }
    this.recomputeHeaderCheck();
  }

  private selectedIds(): string[] {
    return (this.list || []).filter(r => (r as any).selected).map(r => this.getId(r));
  }

  // Actions dropdown: Create Dep JE for selected assets
  onCreateDepJE(): void {
    const ids = this.selectedIds();
    if (!ids.length) {
      this.notify.bsWarning($localize`:Select assets@@selectAssets:Please select one or more assets.`);
      return;
    }

    // fetch config once, then fetch selected assets and post as a batch
    this.faConfigApi.get().pipe(
      switchMap((cfg: any) => {
        const jobs = ids.map(id => this.ds.getById(id));
        return forkJoin(jobs).pipe(
          switchMap(arr => {
            const assets = (arr || []).filter(a => !!a) as any[];
            return this.posting.postDepreciationBatch(assets as any, cfg as any);
          })
        );
      })
    ).subscribe({
      next: () => {
        // refresh current page and clear selection
        this.checkAll = false;
        this.paginate(true);
      },
      error: () => {
        // errors are already surfaced by interceptor/posting service; keep UI responsive
      }
    });
  }

  // Build query string for API fetch
  private buildQuery(): string {
    const q: string[] = [];
    const s = this.vm.search;
    if (s.searchText) {
      q.push(`search=${encodeURIComponent(String(s.searchText))}`);
    }
    if (s.fromDate) {
      const d = new Date(s.fromDate);
      q.push(`fromDate=${encodeURIComponent(d.toISOString())}`);
    }
    if (s.toDate) {
      const d = new Date(s.toDate);
      q.push(`toDate=${encodeURIComponent(d.toISOString())}`);
    }
    if (s.activeOnly) {
      q.push('active=true');
    }
    if (s.location_id) {
      q.push(`location_id=${encodeURIComponent(String(s.location_id))}`);
    }
    q.push(`pageNumber=${Math.max(0, this.page - 1)}`);
    q.push(`pageSize=${this.pageSize}`);
    return '?' + q.join('&');
  }

  // Trigger a new search; if resetPage, go to first page
  search(resetPage: boolean): void {
    if (resetPage) {
      this.page = 1;
    }
    this.paginate(true);
  }

  resetFilters(): void {
    this.vm.search = {searchText: '', fromDate: null, toDate: null, activeOnly: false, location_id: ''};
    this.page = 1;
    this.paginate(true);
  }

  // Use the shared app-search component outputs to drive server-side (mock) search
  // (single canonical implementation)
  serverSearch(criteria: any): void {
    // Normalize incoming criteria keys (varies by emitter across lists)
    const txt = criteria?.searchText ?? criteria?.search_string ?? criteria?.SearchText ?? criteria?.search ?? criteria?.text ?? '';
    const from = criteria?.fromDate ?? criteria?.dateFrom ?? criteria?.startDate ?? criteria?.FromDate ?? null;
    const to = criteria?.toDate ?? criteria?.dateTo ?? criteria?.endDate ?? criteria?.ToDate ?? null;
    const active = criteria?.activeOnly ?? criteria?.active_only ?? criteria?.ActiveOnly ?? false;
    const loc = criteria?.location_id ?? this.vm.search.location_id ?? '';

    this.vm.search = {
      searchText: String(txt || ''),
      fromDate: from || null,
      toDate: to || null,
      activeOnly: !!active,
      location_id: String(loc || '')
    };
    this.search(true);
  }

  // Use the shared app-search component outputs to drive server-side (mock) search
  // serverSearch(criteria: any): void {
  //   // Normalize incoming criteria keys (varies by emitter across lists)
  //   const txt = criteria?.searchText ?? criteria?.SearchText ?? '';
  //   const from = criteria?.fromDate ?? criteria?.FromDate ?? null;
  //   const to = criteria?.toDate ?? criteria?.ToDate ?? null;
  //   const active = criteria?.activeOnly ?? criteria?.ActiveOnly ?? false;
  //   const loc = criteria?.location_id ?? this.vm.search.location_id ?? '';
  //
  //   this.vm.search = {
  //     searchText: String(txt || ''),
  //     fromDate: from || null,
  //     toDate: to || null,
  //     activeOnly: !!active,
  //     location_id: String(loc || '')
  //   };
  //   this.search(true);
  // }

  clearServerSearch(_: any): void {
    this.resetFilters();
  }

  // Fetch current page (API-backed using FixedAssetsService.listApi)
  paginate(withFetch: boolean): void {
    if (!withFetch) {
      if (!this.totalRecords) {
        this.startIndex = 0;
        this.endIndex = 0;
        // Persist pager snapshot
        this.savePagerState();
        return;
      }
      this.startIndex = (this.page - 1) * this.pageSize + 1;
      this.endIndex = Math.min(this.page * this.pageSize, this.totalRecords);
      // Persist pager snapshot
      this.savePagerState();
      return;
    }

    // Use FixedAssetsService to fetch raw API results (no field remapping)
    const selectedIds = new Set((this.list || []).filter(r => (r as any).selected).map(r => this.getId(r)));
    this.assets.listApi(this.vm.search as FixedAssetSearch, this.page, this.pageSize).subscribe({
      next: (res: any) => {
        this.totalRecords = Number(res?.totalRecords ?? 0);
        const arr: any[] = Array.isArray(res?.Data) ? res.Data : [];
        // Preserve selection by Id when reloading current page but apply selection gating
        this.list = arr.map(i => ({...i, selected: selectedIds.has(this.getId(i)) && this.canSelect(i)}));
        this.startIndex = this.totalRecords ? ((this.page - 1) * this.pageSize) + 1 : 0;
        this.endIndex = this.totalRecords ? Math.min(this.page * this.pageSize, this.totalRecords) : 0;
        // Persist pager snapshot after indices known
        this.savePagerState();
        this.isLoaded = true;
        this.recomputeHeaderCheck();
      },
      error: (err) => {
        // Do not double-warn (HTTP interceptor shows toast). Log and keep UI usable.
        try {
          console.log('[FA List][fetch error]', err);
        } catch {
        }
        this.totalRecords = 0;
        this.list = [];
        this.startIndex = 0;
        this.endIndex = 0;
        this.isLoaded = true;
      }
    });
  }

  private savePagerState(): void {
    try {
      const key = this.pagerStatus.buildKey(this.router.url);
      const model: any = {
        lastPage: this.page,
        lastPageSize: this.pageSize,
        totalRecords: this.totalRecords,
        startIndex: this.startIndex,
        endIndex: this.endIndex,
        dateTime: Date.now(),
      };
      this.pagerStatus.storeState(key, model);
    } catch {
    }
  }

  // --- Selection gating helpers ---
  private recomputeHeaderCheck(): void {
    const rows = this.list || [];
    const elig = rows.filter(r => this.canSelect(r));
    this.checkAll = elig.length > 0 && elig.every(r => (r as any).selected === true);
  }

  private parseIsoDate(s?: string): Date | null {
    if (!s) {
      return null;
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  private sameDate(a?: Date | null, b?: Date | null): boolean {
    if (!a || !b) {
      return false;
    }
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private recomputeLatestCompletedEnd(): void {
    const conv: ProrateConvention = (this.config as any)?.prorateConvention || ProrateConvention.Monthly;
    const today = new Date();
    const currentEnd = this.periodEndFor(today.getFullYear(), today.getMonth(), conv);
    let latestCompleted: Date;
    if (currentEnd < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
      latestCompleted = currentEnd;
    } else {
      // use the previous period's end
      const prev = this.previousPeriodEnd(today, conv);
      latestCompleted = prev;
    }
    const y = latestCompleted.getFullYear();
    const m = String(latestCompleted.getMonth() + 1).padStart(2, '0');
    const d = String(latestCompleted.getDate()).padStart(2, '0');
    this.latestCompletedEndIso = `${y}-${m}-${d}`;
  }

  private periodEndFor(year: number, monthIndex0: number, conv: ProrateConvention): Date {
    if (conv === ProrateConvention.Monthly) {
      return new Date(year, monthIndex0 + 1, 0);
    }
    if (conv === ProrateConvention.Quarterly) {
      const q = Math.floor(monthIndex0 / 3);
      const endMonth = q * 3 + 2; // 2,5,8,11
      return new Date(year, endMonth + 1, 0);
    }
    if (conv === ProrateConvention.BiAnnual) {
      const isH1 = monthIndex0 < 6;
      const endMonth = isH1 ? 5 : 11; // Jun or Dec
      return new Date(year, endMonth + 1, 0);
    }
    // Annual
    return new Date(year, 12, 0); // Dec 31
  }

  private previousPeriodEnd(ref: Date, conv: ProrateConvention): Date {
    if (conv === ProrateConvention.Monthly) {
      const y = ref.getFullYear();
      const m0 = ref.getMonth() - 1; // previous month
      const yAdj = m0 < 0 ? y - 1 : y;
      const mAdj = m0 < 0 ? 11 : m0;
      return this.periodEndFor(yAdj, mAdj, conv);
    }
    if (conv === ProrateConvention.Quarterly) {
      const m0 = ref.getMonth();
      const q = Math.floor(m0 / 3);
      const prevQ = (q + 3 - 1) % 4;
      const yearAdj = q === 0 ? ref.getFullYear() - 1 : ref.getFullYear();
      const endMonth = prevQ * 3 + 2;
      return this.periodEndFor(yearAdj, endMonth, conv);
    }
    if (conv === ProrateConvention.BiAnnual) {
      const m0 = ref.getMonth();
      const isH1 = m0 < 6;
      const yearAdj = isH1 ? ref.getFullYear() - 1 : ref.getFullYear();
      const endMonth = isH1 ? 11 : 5; // previous half
      return this.periodEndFor(yearAdj, endMonth, conv);
    }
    // Annual: previous year's Dec 31
    return this.periodEndFor(ref.getFullYear() - 1, 11, ProrateConvention.Annual);
  }

  private getId(row: any): string {
    return String(row?.id ?? row?.Id ?? '');
  }

  canSelect(row: any): boolean {
    // If we don't know the latest completed end, allow selection
    if (!this.latestCompletedEndIso) {
      return true;
    }
    // If the row has no last posted date, allow selection
    const last = (row?.lastPostedDepDate ?? row?.LastPostedDepDate);
    if (!last) {
      return true;
    }
    // Disallow selection if the row is already posted through the latest completed period
    return String(last) !== this.latestCompletedEndIso;
  }
}
